import React from 'react';
import { ArrowRightIcon, HistoryIcon, XIcon } from 'lucide-react';

/*
 * Shown when a counselor clicks Edit on their own application and the camp's form has moved on
 * since they submitted.
 *
 * The point is that neither answer is obviously right, so the person picks rather than the app
 * guessing. Editing against their original keeps the form they recognise and cannot be blocked by
 * a required question they never saw; editing against the updated one is the only way to answer
 * what the camp is asking for now. Whichever they choose is sent as `formVersion` and is what the
 * backend validates against, so the screen and the rules always agree.
 *
 * Not remembered between edits — asked every time. A stored preference would silently keep
 * showing an old form long after the person had forgotten choosing it.
 *
 * Purely presentational, matching EmailConfirmationModal: every decision and all the state live in
 * the parent. Both are Tailwind rather than react-bootstrap like the rest of the app's modals,
 * which would look foreign on this page.
 */

const Choice = ({ title, description, onClick, primary }) => (
    <button
        type="button"
        onClick={onClick}
        className={`tw-group tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-p-4 tw-text-left tw-transition ${
            primary
                ? 'tw-border-brand-600 tw-bg-brand-50 hover:tw-bg-brand-100'
                : 'tw-border-sand-300 tw-bg-white hover:tw-bg-sand-100'
        }`}>
        <span className="tw-min-w-0 tw-flex-1">
            <span className={`tw-block tw-text-sm tw-font-semibold ${primary ? 'tw-text-brand-900' : 'tw-text-gray-900'}`}>
                {title}
            </span>
            <span className="tw-mt-1 tw-block tw-text-xs tw-leading-relaxed tw-text-gray-600">
                {description}
            </span>
        </span>
        <ArrowRightIcon
            className={`tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-transition-transform group-hover:tw-translate-x-0.5 ${
                primary ? 'tw-text-brand-700' : 'tw-text-gray-400'
            }`}
            aria-hidden="true" />
    </button>
);

const FormVersionChoiceModal = ({ submittedVersion, currentVersion, onKeepOriginal, onShowUpdated, onClose }) => (
    <div
        className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-gray-900/50 tw-p-4"
        onClick={onClose}>
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-form-version-heading"
            onClick={event => event.stopPropagation()}
            className="tw-relative tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-xl sm:tw-p-7">
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="tw-absolute tw-right-4 tw-top-4 tw-rounded-lg tw-p-1 tw-text-gray-400 tw-transition hover:tw-bg-sand-100 hover:tw-text-gray-600">
                <XIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
            </button>

            <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-brand-50 tw-text-brand-700">
                <HistoryIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
            </span>
            <h2 id="staff-form-version-heading" className="tw-mt-4 tw-text-lg tw-font-bold tw-text-gray-900">
                This application form has changed
            </h2>
            <p className="tw-mt-1.5 tw-text-sm tw-text-gray-600">
                You applied using version <span className="tw-font-semibold tw-text-gray-900">{submittedVersion}</span> of
                this form. It is now on version <span className="tw-font-semibold tw-text-gray-900">{currentVersion}</span>.
                Which would you like to edit?
            </p>

            <div className="tw-mt-5 tw-space-y-3">
                <Choice
                    title="Keep my original form"
                    description="The questions exactly as you first answered them. Nothing new is asked of you."
                    onClick={onKeepOriginal} />
                <Choice
                    primary
                    title="Show the updated form"
                    description="The camp's current questions, plus anything you answered that it no longer asks. New questions are marked, and any it now requires must be filled in."
                    onClick={onShowUpdated} />
            </div>

            <p className="tw-mt-4 tw-text-xs tw-text-gray-500">
                Your existing answers are kept either way — this only changes which questions you see.
                Nothing is saved until you submit.
            </p>
        </div>
    </div>
);

export default FormVersionChoiceModal;
